import React from 'react';
import { MenuItem } from '@mui/material';
import type { MenuProps } from '@mui/material/Menu';
import type { SxProps, Theme } from '@mui/material/styles';
import { AppOutlinedTextField } from '@/components/shared/AppOutlinedTextField';
import { RECIPE_DIET_LABEL_OPTIONS } from '@/constants/dietary-goals';
import { useClientPreferenceOptions } from '@/hooks/useClientPreferenceOptions';

const TAGS_PLACEHOLDER_COLOR = 'rgba(0, 0, 0, 0.3)';
const TAGS_VALUE_COLOR = 'rgba(0, 0, 0, 0.87)';

const formatUnknownTagLabel = (value: string): string => value
  .replace(/[_-]+/g, ' ')
  .replace(/\b\w/g, (character) => character.toUpperCase());

/** Popover `marginThreshold` shifts tall menus upward and can cover the field; keep anchor stable. */
const tagsSelectMenuProps: Partial<MenuProps> = {
  disableScrollLock: true,
  marginThreshold: null,
};

/** Outlined select styling aligned with recipe Tags frame (Figma DEV). */
const tagsSelectSx: SxProps<Theme> = {
  '& .MuiOutlinedInput-root': {
    minHeight: 56,
    borderRadius: '4px',
    '& fieldset': {
      borderColor: 'rgba(0, 0, 0, 0.23)',
    },
    '&:hover fieldset': {
      borderColor: 'rgba(0, 0, 0, 0.23)',
    },
    '&.Mui-focused fieldset': {
      borderColor: '#52B2D7',
    },
  },
  '& .MuiSelect-select': {
    fontFamily: 'Inter, system-ui, sans-serif',
    fontSize: '1rem',
    fontWeight: 400,
    lineHeight: '24px',
    letterSpacing: '0.15px',
    paddingTop: '16px',
    paddingBottom: '16px',
    paddingLeft: '14px',
    paddingRight: '14px',
  },
  '& .MuiInputLabel-root': {
    color: 'rgba(0, 0, 0, 0.6)',
    '&.Mui-focused': {
      color: 'rgba(0, 0, 0, 0.6)',
    },
  },
};

const tagsMenuItemSx: SxProps<Theme> = {
  fontSize: '1rem',
  fontFamily: 'Inter, system-ui, sans-serif',
  '&.Mui-selected': {
    backgroundColor: 'rgba(82, 178, 215, 0.22)',
    fontWeight: 600,
    color: '#0f172a',
  },
  '&.Mui-selected:hover': {
    backgroundColor: 'rgba(82, 178, 215, 0.32)',
  },
  '&.Mui-selected.Mui-focusVisible': {
    backgroundColor: 'rgba(82, 178, 215, 0.28)',
  },
};

interface RecipeTagsSectionProps {
  allergies: string[];
  intoleranceTags: string[];
  cuisineTypes: string[];
  dietLabels: string[];
  lockedAllergies?: string[];
  lockedIntoleranceTags?: string[];
  onAllergiesChange: (value: string[]) => void;
  onIntoleranceTagsChange: (value: string[]) => void;
  onCuisineTypesChange: (value: string[]) => void;
  onDietLabelsChange: (value: string[]) => void;
}

export const RecipeTagsSection: React.FC<RecipeTagsSectionProps> = ({
  allergies,
  intoleranceTags,
  cuisineTypes,
  dietLabels,
  lockedAllergies = [],
  lockedIntoleranceTags = [],
  onAllergiesChange,
  onIntoleranceTagsChange,
  onCuisineTypesChange,
  onDietLabelsChange,
}) => {
  const {
    allergies: allergyOptions,
    foodIntolerances: intoleranceOptions,
    cuisineTypes: cuisineOptions,
  } = useClientPreferenceOptions();

  const lockedAllergySet = React.useMemo(
    () => new Set(lockedAllergies.map((value) => String(value).trim()).filter(Boolean)),
    [lockedAllergies],
  );
  const lockedIntoleranceSet = React.useMemo(
    () => new Set(lockedIntoleranceTags.map((value) => String(value).trim()).filter(Boolean)),
    [lockedIntoleranceTags],
  );

  return (
    <div
      className="grid grid-cols-1 gap-x-1.5 gap-y-5 md:grid-cols-2"
      style={{ fontFamily: 'Inter, system-ui, sans-serif' }}
    >
      <div className="relative z-[55] min-w-0">
        <AppOutlinedTextField
          select
          fullWidth
          size="small"
          label="Diet Labels"
          value={dietLabels}
          onChange={(e) => {
            const value = e.target.value;
            onDietLabelsChange(typeof value === 'string' ? value.split(',') : (value as string[]));
          }}
          SelectProps={{
            multiple: true,
            displayEmpty: true,
            MenuProps: tagsSelectMenuProps,
            renderValue: (selected) => {
              const values = selected as string[];
              if (!values || values.length === 0) {
                return <span style={{ color: TAGS_PLACEHOLDER_COLOR }}>Select Labels</span>;
              }
              const labels = RECIPE_DIET_LABEL_OPTIONS
                .filter((option) => values.includes(option.value))
                .map((option) => option.name);
              // Fallback for custom values not in RECIPE_DIET_LABEL_OPTIONS
              const unknownValues = values.filter((v) => !RECIPE_DIET_LABEL_OPTIONS.some((o) => o.value === v));
              return <span style={{ color: TAGS_VALUE_COLOR }}>{[...labels, ...unknownValues.map(formatUnknownTagLabel)].join(', ')}</span>;
            },
          }}
          sx={tagsSelectSx}
        >
          {RECIPE_DIET_LABEL_OPTIONS.map((option) => (
            <MenuItem key={option.value} value={option.value} sx={tagsMenuItemSx}>
              {option.name}
            </MenuItem>
          ))}
          {dietLabels
            .filter((value) => !RECIPE_DIET_LABEL_OPTIONS.some((option) => option.value === value))
            .map((value) => (
              <MenuItem key={value} value={value} sx={tagsMenuItemSx}>
                {formatUnknownTagLabel(value)}
              </MenuItem>
            ))}
        </AppOutlinedTextField>
      </div>

      <div className="relative z-[54] min-w-0">
        <AppOutlinedTextField
          select
          fullWidth
          size="small"
          label="Allergens"
          value={allergies}
          onChange={(e) => {
            const value = e.target.value;
            const nextValues = typeof value === 'string' ? value.split(',') : (value as string[]);
            onAllergiesChange(Array.from(new Set([...nextValues, ...lockedAllergies])));
          }}
          SelectProps={{
            multiple: true,
            displayEmpty: true,
            MenuProps: tagsSelectMenuProps,
            renderValue: (selected) => {
              const values = selected as string[];
              if (!values || values.length === 0) {
                return <span style={{ color: TAGS_PLACEHOLDER_COLOR }}>Select Allergens</span>;
              }
              const labels = allergyOptions
                .filter((allergy) => values.includes(allergy.value))
                .map((allergy) => allergy.name);
              const unknownValues = values.filter((value) => !allergyOptions.some((allergy) => allergy.value === value));
              return <span style={{ color: TAGS_VALUE_COLOR }}>{[...labels, ...unknownValues.map(formatUnknownTagLabel)].join(', ')}</span>;
            },
          }}
          sx={tagsSelectSx}
        >
          {allergyOptions.map((allergy) => (
            <MenuItem
              key={allergy.value}
              value={allergy.value}
              disabled={lockedAllergySet.has(allergy.value)}
              sx={tagsMenuItemSx}
            >
              {allergy.name}
            </MenuItem>
          ))}
          {allergies
            .filter((value) => !allergyOptions.some((allergy) => allergy.value === value))
            .map((value) => (
              <MenuItem
                key={value}
                value={value}
                disabled={lockedAllergySet.has(value)}
                sx={tagsMenuItemSx}
              >
                {formatUnknownTagLabel(value)}
              </MenuItem>
            ))}
        </AppOutlinedTextField>
      </div>

      <div className="relative z-[53] min-w-0">
        <AppOutlinedTextField
          select
          fullWidth
          size="small"
          label="Intolerance Tags"
          value={intoleranceTags}
          onChange={(e) => {
            const value = e.target.value;
            const nextValues = typeof value === 'string' ? value.split(',') : (value as string[]);
            onIntoleranceTagsChange(Array.from(new Set([...nextValues, ...lockedIntoleranceTags])));
          }}
          SelectProps={{
            multiple: true,
            displayEmpty: true,
            MenuProps: tagsSelectMenuProps,
            renderValue: (selected) => {
              const values = selected as string[];
              if (!values || values.length === 0) {
                return <span style={{ color: TAGS_PLACEHOLDER_COLOR }}>Select Intolerance tags</span>;
              }
              const labels = intoleranceOptions
                .filter((tag) => values.includes(tag.value))
                .map((tag) => tag.name);
              const unknownValues = values.filter((value) => !intoleranceOptions.some((tag) => tag.value === value));
              return <span style={{ color: TAGS_VALUE_COLOR }}>{[...labels, ...unknownValues.map(formatUnknownTagLabel)].join(', ')}</span>;
            },
          }}
          sx={tagsSelectSx}
        >
          {intoleranceOptions.map((tag) => (
            <MenuItem
              key={tag.value}
              value={tag.value}
              disabled={lockedIntoleranceSet.has(tag.value)}
              sx={tagsMenuItemSx}
            >
              {tag.name}
            </MenuItem>
          ))}
          {intoleranceTags
            .filter((value) => !intoleranceOptions.some((tag) => tag.value === value))
            .map((value) => (
              <MenuItem
                key={value}
                value={value}
                disabled={lockedIntoleranceSet.has(value)}
                sx={tagsMenuItemSx}
              >
                {formatUnknownTagLabel(value)}
              </MenuItem>
            ))}
        </AppOutlinedTextField>
      </div>

      <div className="relative z-[52] min-w-0">
        <AppOutlinedTextField
          select
          fullWidth
          size="small"
          label="Cuisine Types"
          value={cuisineTypes}
          onChange={(e) => {
            const value = e.target.value;
            onCuisineTypesChange(typeof value === 'string' ? value.split(',') : (value as string[]));
          }}
          SelectProps={{
            multiple: true,
            displayEmpty: true,
            MenuProps: tagsSelectMenuProps,
            renderValue: (selected) => {
              const values = selected as string[];
              if (!values || values.length === 0) {
                return <span style={{ color: TAGS_PLACEHOLDER_COLOR }}>Select Cuisine Types</span>;
              }
              const labels = cuisineOptions
                .filter((cuisine) => values.includes(cuisine.value))
                .map((cuisine) => cuisine.name);
              const unknownValues = values.filter((value) => !cuisineOptions.some((cuisine) => cuisine.value === value));
              return <span style={{ color: TAGS_VALUE_COLOR }}>{[...labels, ...unknownValues.map(formatUnknownTagLabel)].join(', ')}</span>;
            },
          }}
          sx={tagsSelectSx}
        >
          {cuisineOptions.map((cuisine) => (
            <MenuItem
              key={cuisine.value}
              value={cuisine.value}
              sx={[tagsMenuItemSx, { pl: cuisine.parentId ? 4 : 2 }] as SxProps<Theme>}
            >
              {cuisine.name}
            </MenuItem>
          ))}
          {cuisineTypes
            .filter((value) => !cuisineOptions.some((cuisine) => cuisine.value === value))
            .map((value) => (
              <MenuItem key={value} value={value} sx={tagsMenuItemSx}>
                {formatUnknownTagLabel(value)}
              </MenuItem>
            ))}
        </AppOutlinedTextField>
      </div>
    </div>
  );
};
